import { formatMoney } from '@theme/utilities';
import { ThemeEvents, ToggleCrossSellsUpdatedEvent } from '@theme/events';

// Ultimadesk : frise des cross-sells (reprise de Desktronic). Dès que deux listes du formulaire ont une étape
// (« Chaises », « Accessoires »), une seule liste est affichée à la fois sous une frise : un nœud par liste, coché
// dès qu'un produit y est choisi, et le trait se remplit jusqu'à l'étape suivante, qui s'annonce. Un clic sur un
// nœud affiche sa liste. Les produits cochés dans une liste masquée restent ajoutés au panier (product-form.js).
// Construite ici et non en Liquid : un bloc ne connaît pas les autres listes du formulaire.
const pendingStepScopes = new Set();
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function scheduleCrossSellSteps(scope) {
  if (scope == null || pendingStepScopes.has(scope)) return;
  pendingStepScopes.add(scope);

  // Une seule construction quand plusieurs listes se connectent ensemble (chargement, éditeur).
  // Micro-tâche et non requestAnimationFrame, suspendu tant que l'onglet n'est pas affiché.
  queueMicrotask(() => {
    pendingStepScopes.delete(scope);
    buildCrossSellSteps(scope);
  });
}

function buildCrossSellSteps(scope) {
  scope.querySelectorAll('[data-ref="ud-xs-steps"]').forEach((steps) => steps.remove());

  const lists = [...scope.querySelectorAll('toggle-cross-sell-list[data-step-label]')];
  if (lists.length < 2) {
    lists.forEach((list) => list.classList.remove('ud-xs-step-hidden'));
    return;
  }

  const nav = document.createElement('nav');
  nav.className = 'ud-xs-steps';
  nav.dataset.ref = 'ud-xs-steps';
  nav.setAttribute('aria-label', 'Compléter votre équipement');
  nav.scope = scope;
  nav.lists = lists;

  const fill = document.createElement('span');
  fill.className = 'ud-xs-steps__fill';
  fill.setAttribute('aria-hidden', 'true');

  const stepList = document.createElement('ol');
  stepList.className = 'ud-xs-steps__list';

  lists.forEach((list, index) => {
    const step = document.createElement('li');
    step.className = 'ud-xs-steps__step';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ud-xs-steps__button';
    button.setAttribute('aria-controls', list.id);
    button.innerHTML = `
      <span class="ud-xs-steps__node" aria-hidden="true"></span>
      <span class="ud-xs-steps__label"></span>
      <span class="ud-xs-steps__status"></span>`;
    button.querySelector('.ud-xs-steps__label').textContent = list.dataset.stepLabel;
    button.addEventListener('click', () => showCrossSellStep(nav, index));

    step.appendChild(button);
    stepList.appendChild(step);
  });

  nav.append(fill, stepList);
  lists[0].before(nav);

  // Après une reconstruction (éditeur), on reste sur l'étape affichée
  showCrossSellStep(nav, Math.min(scope.udStepsCurrent ?? 0, lists.length - 1), false);
}

function showCrossSellStep(nav, index, animate = true) {
  const previous = nav.current;
  nav.current = index;
  nav.scope.udStepsCurrent = index;

  nav.lists.forEach((list, listIndex) => {
    list.classList.toggle('ud-xs-step-hidden', listIndex !== index);
    list.classList.remove('ud-xs-step-in-next', 'ud-xs-step-in-prev');
  });

  const list = nav.lists[index];
  if (animate && previous != null && previous !== index && !reduceMotion.matches) {
    list.addEventListener('animationend', () => list.classList.remove('ud-xs-step-in-next', 'ud-xs-step-in-prev'), { once: true });
    list.classList.add(index > previous ? 'ud-xs-step-in-next' : 'ud-xs-step-in-prev');
  }

  // Flèches du slider : calculées pendant que la liste était masquée
  list.updateNav?.();
  updateCrossSellSteps(nav);
}

function updateCrossSellSteps(nav, selectionChanged = false) {
  const steps = [...nav.querySelectorAll('.ud-xs-steps__step')];
  const counts = nav.lists.map((list) => list.getSelectedItems?.().items.length ?? 0);
  const lastDone = counts.reduce((last, count, index) => (count > 0 ? index : last), -1);
  // Le trait va jusqu'à l'étape affichée, ou jusqu'à celle qui suit le dernier choix
  const reach = Math.min(Math.max(nav.current, lastDone + 1), steps.length - 1);

  nav.style.setProperty('--ud-xs-reach', reach / (steps.length - 1));
  nav.classList.toggle('is-complete', counts.every((count) => count > 0));

  steps.forEach((step, index) => {
    const count = counts[index];
    const isNext = index === reach && index > nav.current && count === 0;
    const becomesNext = isNext && !step.classList.contains('is-next');

    step.classList.toggle('is-done', count > 0);
    step.classList.toggle('is-current', index === nav.current);
    step.classList.toggle('is-reached', index <= reach);
    step.classList.toggle('is-next', isNext);
    step.querySelector('.ud-xs-steps__button').setAttribute('aria-current', index === nav.current ? 'step' : 'false');
    step.querySelector('.ud-xs-steps__status').textContent =
      count === 0 ? 'Aucun' : `${count} sélectionné${count > 1 ? 's' : ''}`;

    if (becomesNext && selectionChanged && !reduceMotion.matches) {
      step.classList.remove('is-pulsing');
      void step.offsetWidth;
      step.classList.add('is-pulsing');
      step.querySelector('.ud-xs-steps__node').addEventListener('animationend', () => step.classList.remove('is-pulsing'), { once: true });
    }
  });
}

document.addEventListener(ThemeEvents.toggleCrossSellsUpdated, () => {
  document.querySelectorAll('[data-ref="ud-xs-steps"]').forEach((nav) => updateCrossSellSteps(nav, true));
});

// Éditeur : la liste sélectionnée dans le panneau s'affiche, même si ce n'est pas l'étape en cours
document.addEventListener('shopify:block:select', (event) => {
  const list = event.target.closest?.('toggle-cross-sell-list');
  if (list == null) return;

  document.querySelectorAll('[data-ref="ud-xs-steps"]').forEach((nav) => {
    const index = nav.lists.indexOf(list);
    if (index >= 0) showCrossSellStep(nav, index, false);
  });
});

class ToggleCrossSellList extends HTMLElement {
  constructor() {
    super();

    this.crossSells = null;
  }

  connectedCallback() {
    this.crossSells = this.querySelectorAll('[data-ref="toggle-cross-sell"]');

    // Ultimadesk : frise de progression (listes avec une étape dans le même formulaire)
    this.stepsScope = this.closest('.product-form__form') || this.parentElement;
    scheduleCrossSellSteps(this.stepsScope);

    // Ultimadesk : flèches du slider (présentation « Cartes en slider »)
    this.track = this.querySelector('[data-ref="ud-xs-track"]');
    if (this.track == null) return;

    this.prevButton = this.querySelector('[data-ref="ud-xs-prev"]');
    this.nextButton = this.querySelector('[data-ref="ud-xs-next"]');
    this.prevButton?.addEventListener('click', () => this.scrollTrack(-1));
    this.nextButton?.addEventListener('click', () => this.scrollTrack(1));
    this.track.addEventListener('scroll', this.updateNav.bind(this), { passive: true });
    window.addEventListener('resize', this.updateNav.bind(this));
    this.updateNav();
  }

  disconnectedCallback() {
    // Ultimadesk : liste retirée (éditeur) : la frise est reconstruite sans elle
    if (this.stepsScope?.isConnected) scheduleCrossSellSteps(this.stepsScope);
  }

  scrollTrack(direction) {
    const card = this.track.querySelector('[data-ref="toggle-cross-sell"]');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = card ? card.offsetWidth + gap : this.track.clientWidth;

    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  updateNav() {
    if (this.track == null) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;

    if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll;

    // Ultimadesk : bord fondu seulement du côté où des cartes dépassent
    this.track.classList.toggle('has-more-start', this.track.scrollLeft > 2);
    this.track.classList.toggle('has-more-end', this.track.scrollLeft < maxScroll);
  }

  getSelectedItems() {
    let items = [];
    let total_price = 0;
    let total_compare_at_price = 0;

    this.crossSells.forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      const button = crossSell.querySelector('[data-ref="toggle-cross-sell-button"]');

      if (checkbox != null && checkbox.checked == true) {
        items.push({
          id: checkbox.dataset.variantId,
          quantity: 1,
        });

        total_price += parseInt(crossSell.getPrices().price);
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice);
      }

      if (button != null && button.classList.contains('button--active') == true) {
        items.push({
          id: button.dataset.variantId,
          quantity: 1,
        });

        total_price += parseInt(crossSell.getPrices().price);
        total_compare_at_price += parseInt(crossSell.getPrices().compareAtPrice);
      }
    });

    return { items: items, total_price: total_price, total_compare_at_price: total_compare_at_price };
  }
}

if (!customElements.get('toggle-cross-sell-list')) {
  customElements.define('toggle-cross-sell-list', ToggleCrossSellList);
}

class ToggleCrossSell extends HTMLElement {
  constructor() {
    super();

    this.checkbox = null;
    this.button = null;
    this.select = null;
    this.image = null;
    this.price = null;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
    this.button = this.querySelector('[data-ref="toggle-cross-sell-button"]');
    this.select = this.querySelector('[data-ref="toggle-cross-sell-select"]');
    this.image = this.querySelector('[data-ref="toggle-cross-sell-image"]');
    this.price = this.querySelector('[data-ref="toggle-cross-sell-price"]');

    this.select?.addEventListener('change', this.variantChanged.bind(this));
    this.button?.addEventListener('click', this.toggleAddButton.bind(this));
    this.checkbox?.addEventListener('change', this.checkboxChanged.bind(this));
    this.addEventListener('click', this.handleCardClick.bind(this));

    if (this.classList.contains('ud-xs-card')) this.initCard();
  }

  // Ultimadesk : carte « Cartes en slider » (variantes en pastilles, fenêtre Détails)
  initCard() {
    const variantsScript = this.querySelector('[data-ref="toggle-cross-sell-variants"]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    this.currentVariant = this.variants.find((variant) => String(variant.id) === this.checkbox?.dataset.variantId) || this.variants[0];

    // Image de départ (photo détourée éventuelle), remise quand on revient à cette variante
    const colorImagesScript = this.querySelector('[data-ref="toggle-cross-sell-color-images"]');
    this.colorImages = colorImagesScript ? JSON.parse(colorImagesScript.textContent) : [];
    this.initialVariantId = String(this.currentVariant?.id);
    this.initialImage = this.image?.innerHTML;
    this.initialCutout = this.image?.classList.contains('ud-xs-card__image--cutout');

    this.querySelectorAll('[data-ref="toggle-cross-sell-option"]').forEach((optionButton) => {
      optionButton.addEventListener('click', this.optionClicked.bind(this, optionButton));
    });
    if (this.currentVariant) this.syncOptions(this.currentVariant);

    this.details = this.querySelector('dialog-component');
    this.querySelector('[data-ref="popup-toggle"]')?.addEventListener('click', this.detailsOpened.bind(this));
    this.querySelector('[data-ref="toggle-cross-sell-modal-add"]')?.addEventListener('click', this.modalAddClicked.bind(this));

    this.modalTrack = this.querySelector('[data-ref="ud-xs-modal-track"]');
    this.modalDots = this.querySelector('[data-ref="ud-xs-modal-dots"]');
    this.querySelector('[data-ref="ud-xs-modal-prev"]')?.addEventListener('click', () => this.scrollModal(-1));
    this.querySelector('[data-ref="ud-xs-modal-next"]')?.addEventListener('click', () => this.scrollModal(1));
    this.modalTrack?.addEventListener('scroll', this.updateModalDots.bind(this), { passive: true });
  }

  detailsOpened() {
    this.filterGallery();
    this.loadFeatures();
  }

  getVisibleSlides() {
    if (this.modalTrack == null) return [];
    return [...this.modalTrack.children].filter((slide) => !slide.hidden);
  }

  // Galerie : images de la couleur choisie + images génériques (convention « Couleur | alt »)
  filterGallery() {
    if (this.modalTrack == null) return;

    const values = (this.currentVariant?.options || []).map((value) => String(value).toLowerCase());
    const slides = [...this.modalTrack.children];
    const hasMatch = slides.some((slide) => slide.dataset.color && values.includes(slide.dataset.color));

    slides.forEach((slide) => {
      slide.hidden = hasMatch && slide.dataset.color !== '' && !values.includes(slide.dataset.color);
    });

    this.modalTrack.scrollLeft = 0;
    this.buildModalDots();
  }

  buildModalDots() {
    if (this.modalDots == null) return;

    const slides = this.getVisibleSlides();
    this.modalDots.innerHTML = '';
    this.modalDots.hidden = slides.length < 2;

    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `Image ${index + 1} sur ${slides.length}`);
      dot.addEventListener('click', () => this.modalTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' }));
      this.modalDots.appendChild(dot);
    });

    this.updateModalDots();
  }

  updateModalDots() {
    if (this.modalDots == null || this.modalTrack.clientWidth === 0) return;

    const index = Math.round(this.modalTrack.scrollLeft / this.modalTrack.clientWidth);
    [...this.modalDots.children].forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
  }

  scrollModal(direction) {
    const width = this.modalTrack.clientWidth;
    const count = this.getVisibleSlides().length;
    const index = Math.round(this.modalTrack.scrollLeft / width);
    const target = (index + direction + count) % count;

    this.modalTrack.scrollTo({ left: target * width, behavior: 'smooth' });
  }

  // Reprend le premier accordéon de la fiche produit (« Avantages et caractéristiques »)
  async loadFeatures() {
    const features = this.querySelector('[data-ref="ud-xs-features"]');
    if (features == null || this.featuresLoaded) return;
    this.featuresLoaded = true;

    try {
      const response = await fetch(features.dataset.url);
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const mainSection = html.querySelector('[id^="shopify-section-template"][id$="__main"]') || html;
      const accordion = mainSection.querySelector('details.accordion');
      const summary = accordion?.querySelector('summary');
      if (accordion == null || summary == null) return;

      const title = summary.textContent.trim().replace(/\s+/g, ' ');
      if (title) this.querySelector('[data-ref="ud-xs-features-title"]').textContent = title;

      const content = this.querySelector('[data-ref="ud-xs-features-content"]');
      [...accordion.children].forEach((child) => {
        if (child === summary) return;
        child.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        content.appendChild(document.importNode(child, true));
      });

      if (content.textContent.trim() !== '') features.hidden = false;
    } catch (error) {
      this.featuresLoaded = false;
    }
  }

  optionClicked(optionButton, event) {
    event.preventDefault();

    const position = parseInt(optionButton.closest('[data-option-position]').dataset.optionPosition);
    const variant = this.findVariant(position, optionButton.dataset.value);
    if (variant == null) return;

    this.currentVariant = variant;
    this.syncOptions(variant);
    this.applyVariant(variant);
  }

  // Ultimadesk : variante qui garde la valeur cliquée et les options qui la précèdent, au plus près des autres
  // options choisies, disponible de préférence. Une combinaison inexistante (T300 : Chêne seulement en 140 × 60 cm)
  // ne trouvait aucune variante : la pastille changeait, mais le prix, l'image et le produit ajouté restaient ceux
  // de l'ancienne couleur.
  findVariant(position, value) {
    const wanted = [...(this.currentVariant?.options || [])];
    wanted[position - 1] = value;

    const score = (variant) =>
      variant.options.filter((option, index) => option === wanted[index]).length * 2 + (variant.available ? 1 : 0);

    return this.variants
      .filter((variant) => variant.options.slice(0, position).every((option, index) => option === wanted[index]))
      .sort((a, b) => score(b) - score(a))[0];
  }

  // Ultimadesk : boutons de la carte et de la fenêtre Détails alignés sur la variante. Une valeur impossible avec
  // les options qui la précèdent est masquée ; une option qui ne laisse plus qu'un choix (la taille d'une couleur)
  // est masquée sur la carte (ud-xs-options--single), mais reste affichée dans la fenêtre Détails.
  syncOptions(variant) {
    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const index = parseInt(group.dataset.optionPosition) - 1;
      const previous = variant.options.slice(0, index);
      let possibleCount = 0;

      group.querySelectorAll('[data-ref="toggle-cross-sell-option"]').forEach((button) => {
        const value = button.dataset.value;
        const selected = value === variant.options[index];
        const possible = this.variants.some(
          (candidate) => candidate.options[index] === value && previous.every((option, i) => candidate.options[i] === option)
        );

        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.hidden = !possible;
        if (possible) possibleCount += 1;
      });

      group.closest('.ud-xs-options')?.classList.toggle('ud-xs-options--single', possibleCount < 2);
    });

    this.querySelectorAll('[data-ref="ud-xs-option-value"]').forEach((label) => {
      const value = variant.options[parseInt(label.dataset.position) - 1];
      if (value != null) label.textContent = value;
    });
  }

  applyVariant(variant) {
    const price = variant.price;
    const compareAtPrice = variant.compare_at_price > variant.price ? variant.compare_at_price : null;

    this.dataset.price = price;
    if (compareAtPrice != null) {
      this.dataset.compareAtPrice = compareAtPrice;
    } else {
      delete this.dataset.compareAtPrice;
    }

    this.querySelectorAll('[data-ref="toggle-cross-sell-price"], [data-ref="toggle-cross-sell-modal-price"]').forEach((container) => {
      const priceElement = container.querySelector('[data-ref="price"]');
      const compareElement = container.querySelector('[data-ref="compare-at-price"]');
      const savingsElement = container.querySelector('[data-ref="difference-price-value"]');

      if (priceElement) priceElement.innerHTML = formatMoney(price);
      if (compareElement) compareElement.innerHTML = compareAtPrice != null ? formatMoney(compareAtPrice) : '';
      if (savingsElement) savingsElement.innerHTML = this.formatSavings(price, compareAtPrice);
    });

    this.updateCardImage(variant);
    this.filterGallery();

    if (this.checkbox != null) {
      this.checkbox.dataset.variantId = variant.id;
      this.checkbox.disabled = !variant.available;
      this.checkbox.checked = variant.available;
    }

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  // Image de la carte : celle de la variante, à défaut la première image dont le texte alternatif
  // commence par la couleur choisie (« Noir | … ») ; l'image de départ revient avec sa variante
  updateCardImage(variant) {
    if (this.image == null) return;

    if (String(variant.id) === this.initialVariantId && this.initialImage != null) {
      this.image.innerHTML = this.initialImage;
      this.image.classList.toggle('ud-xs-card__image--cutout', this.initialCutout);
      return;
    }

    const values = (variant.options || []).map((value) => String(value).toLowerCase());
    const featured = variant.featured_image?.src;
    const src = featured
      ? `${featured}${featured.includes('?') ? '&' : '?'}width=400`
      : this.colorImages.find(({ color }) => values.includes(color))?.src;
    if (!src) return;

    let img = this.image.querySelector('img');
    if (img == null) {
      img = document.createElement('img');
      img.alt = '';
      this.image.replaceChildren(img);
    }
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = src;
    this.image.classList.remove('ud-xs-card__image--cutout');
  }

  formatSavings(price, compareAtPrice) {
    if (compareAtPrice == null) return '';

    if (this.dataset.salesBadge === 'percentage') {
      return `-${Math.round(((compareAtPrice - price) * 100) / compareAtPrice)}%`;
    }

    return `-${formatMoney(compareAtPrice - price)}`;
  }

  modalAddClicked(event) {
    event.preventDefault();
    if (this.checkbox == null || this.checkbox.disabled) return;

    this.checkbox.checked = !this.checkbox.checked;
    this.checkboxChanged();
    this.details?.close();
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.variantChanged.bind(this));
    this.button?.removeEventListener('click', this.toggleAddButton.bind(this));
    this.removeEventListener('click', this.handleCardClick.bind(this));
  }

  handleCardClick(event) {
    // Laisse la redirection du lien produit et le changement de variante intacts
    if (event.target.closest('a') != null) return;
    if (event.target.closest('.select-wrapper') != null) return;
    // Ultimadesk : boutons de variantes et fenêtre Détails des cartes
    if (event.target.closest('[data-ref="toggle-cross-sell-option"]') != null) return;
    if (event.target.closest('dialog-component') != null) return;

    // Les clics directs sur la checkbox ou le bouton sont gérés par leurs propres écouteurs
    const checkboxLabel = this.checkbox?.closest('.checkbox');
    if (checkboxLabel != null && checkboxLabel.contains(event.target)) return;
    if (this.button != null && this.button.contains(event.target)) return;

    if (this.checkbox != null) {
      if (this.checkbox.disabled) return;
      this.checkbox.checked = !this.checkbox.checked;
      this.checkboxChanged();
    } else if (this.button != null) {
      this.toggleAddButton();
    }
  }

  checkboxChanged() {
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  toggleAddButton(active) {
    if (this.button.classList.contains('button--active') == false || active == true) {
      this.button.classList.add('button--active');
      this.button.classList.remove('button--secondary');
    } else {
      this.button.classList.remove('button--active');
      this.button.classList.add('button--secondary');
    }

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  variantChanged() {
    const optionSelected = this.select.options[this.select.selectedIndex];

    this.updateProductPrice(optionSelected);
    this.updateProductImage(optionSelected);
    this.updateVariantId(optionSelected);

    if (this.checkbox != null) this.checkbox.checked = true;
    if (this.button != null) this.toggleAddButton(true);

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  updateProductPrice(optionSelected) {
    const variantPrice = optionSelected.dataset.price;
    const variantCompareAtPrice = optionSelected.dataset.compareAtPrice;

    this.price.querySelector('.price').innerHTML = formatMoney(variantPrice);
    this.dataset.price = variantPrice;

    if (variantCompareAtPrice != null) {
      this.price.querySelector('.compare-at-price').innerHTML = formatMoney(variantCompareAtPrice);
      this.dataset.compareAtPrice = variantCompareAtPrice;
    }
  }

  updateProductImage(optionSelected) {
    const variantImage = optionSelected.dataset.variantImage;

    if (variantImage != null && this.image != null) {
      this.image.querySelector('img').src = variantImage;
    }
  }

  updateVariantId(optionSelected) {
    const variantId = optionSelected.value;

    if (this.checkbox != null) this.checkbox.dataset.variantId = variantId;
    if (this.button != null) this.button.dataset.variantId = variantId;
  }

  getPrices() {
    let price = 0;
    let compareAtPrice = 0;

    if (this.dataset.price != null) {
      price = parseInt(this.dataset.price);
    }

    if (this.dataset.compareAtPrice != null) {
      compareAtPrice = parseInt(this.dataset.compareAtPrice);
    } else {
      compareAtPrice = parseInt(this.dataset.price);
    }

    return {
      price: price,
      compareAtPrice: compareAtPrice,
    };
  }
}

if (!customElements.get('toggle-cross-sell')) {
  customElements.define('toggle-cross-sell', ToggleCrossSell);
}
